import { Button } from "@msuite/ui/button";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { type RowActionsProps, RowActionsWrapper } from "@msuite/ui/table";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import type { RoleMember } from "@msuite/utils/types";
import { getFullName } from "@msuite/utils/users";
import { Fragment } from "react";
import { client } from "@/hono";

/**
 * The one action a member row offers: removing the person from the role, behind the app-wide
 * confirmation dialog — the write revokes permissions the moment it lands, so it is not a
 * click that may happen in passing.
 */
export function RoleMemberRowActions({
	row,
	roleId,
}: RowActionsProps<RoleMember> & { roleId: string }) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const asyncModal = useAsyncModal();

	/** Functions */
	async function handleOnRemove() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		try {
			const response = await client.roles[":roleId"].members[":userId"].$delete({
				param: { roleId, userId: row.original.id },
			});
			// One message per outcome, raised here: the handler's own toast is suppressed so a
			// failure cannot end up reported twice, or — when the request never reaches the
			// server — not at all.
			await responseHandler(response, false);
			toast.success(t("roles.member_removed"));
			// One write, three readers: the roles list's member counts, the remover's own
			// abilities, and the user page's permission grid — see the note in
			// `role-member-select.tsx`.
			await qc.invalidateQueries({
				predicate: invalidate(["roles", "citadel", "user-permissions-form"]),
			});
		} catch (error) {
			toast.error(error instanceof Error ? error.message : t("general.something_went_wrong"));
		}
	}

	/** Render */
	return (
		<Fragment>
			<RowActionsWrapper>
				<Menu
					items={() => [
						{
							label: t("roles.remove_member"),
							icon: icon("UserRoundMinus"),
							onClick: handleOnRemove,
							isDanger: true,
							cyId: `role-member-remove-item-${row.original.id}`,
						},
					]}
				>
					<Button
						size="xxs"
						variant="ghost"
						icon={<Icon name="EllipsisVertical" />}
						data-cy={`role-member-row-menu-${row.original.id}`}
					/>
				</Menu>
			</RowActionsWrapper>
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					title={t("roles.remove_member")}
					message={t("roles.remove_member_confirmation", { name: getFullName(row.original) })}
					confirmLabel={t("general.remove")}
				/>
			)}
		</Fragment>
	);
}
